import { Injectable } from '@angular/core';
import { CollabCommitMergeResult } from '@common/collab/models/collab-commit-result.model';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { WorkspaceFileUserStatus } from '@common/workspace/enums/workspace-file-user-status.enum';
import { WorkspaceFileUser } from '@common/workspace/models/workspace-file-user.model';
import { WorkspaceFile } from '@common/workspace/models/workspace-file.model';
import { WorkspacePullRequestBody } from '@common/workspace/models/workspace-pull-request-body.model';
import { CollabApiService } from '@portal-core/collab/services/collab-api.service';
import { CollectionServiceBase } from '@portal-core/data/collection/services/collection.service.base';
import { GetDataOptions } from '@portal-core/data/common/models/get-data-options.model';
import { DataService } from '@portal-core/data/common/services/data.service';
import { WorkspaceFileDataService } from '@portal-core/project-files/workspace/services/workspace-file-data.service';
import { Resettable } from '@portal-core/util/resettable.decorator';
import { uniqBy } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
@Resettable()
export class WorkspaceFileService extends CollectionServiceBase<WorkspaceFile> {

  /* To help show when the workspace grid finishes loading. */
  public workspaceItemsLoaded: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(
    private collabApiService: CollabApiService,
    private workspaceFileDataService: WorkspaceFileDataService,
    protected dataService: DataService
  ) {
    super(workspaceFileDataService, dataService);
  }

  getWorkspaceFile(projectId: number, branchName: string, filePath: string): WorkspaceFile {
    return this.workspaceFileDataService.getItemByProperties('projectId', projectId, 'branchName', branchName, 'filePath', filePath);
  }

  getUniqueWorkspaceFileActiveUserIds(workspaceFiles: WorkspaceFile[], invokerUserId: string): string[] {
    return [
      ...new Set(
        workspaceFiles.flatMap(workspaceFile => {
          return workspaceFile.activeUsers
            ?.filter(collabUser => collabUser.userId && collabUser.userId !== invokerUserId)
            ?.map(collabUser => collabUser.userId);
        })
      )
    ];
  }

  getUniqueWorkspaceFileUsers(workspaceFiles: WorkspaceFile[], invokerUserId: string, status?: WorkspaceFileUserStatus): WorkspaceFileUser[] {
    return uniqBy(
      workspaceFiles.flatMap(workspaceFile => {
        return workspaceFile.userStatuses?.filter(workspaceFileUser => {
          return workspaceFileUser.userId && workspaceFileUser.userId !== invokerUserId && (!status || workspaceFileUser.status === status);
        });
      }).filter(workspaceFileUser => !!workspaceFileUser),
      'userId'
    );
  }

  getWorkspaceFile$(projectId: number, branchName: string, filePath: string, options: GetDataOptions = null): Observable<WorkspaceFile> {
    return this.dataService.getData<WorkspaceFile>({
      get: () => this.workspaceFileDataService.getItemByProperties$('projectId', projectId, 'branchName', branchName, 'filePath', filePath),
      fetch: () => this.collabApiService.getWorkspaceFile$(projectId, branchName, filePath),
      set: collabWorkspaceFileMongoItem => this.workspaceFileDataService.addItems$([collabWorkspaceFileMongoItem])
    }, options);
  }

  getWorkspaceFilesPage$(filter: PageFilter, projectId: number, branchName: string): Observable<Page<WorkspaceFile>> {
    return this.collabApiService.getWorkspaceFilesPage$(filter, projectId, branchName);
  }

  bulkUpdateWorkspaceFileStatus$(projectId: number, docIds: string[], schemaNames: string[], userId: string, status: WorkspaceFileUserStatus, docVersions?: number[]): Observable<any> {
    return this.collabApiService.bulkUpdateWorkspaceFileStatus$(projectId, docIds, schemaNames, userId, status, docVersions);
  }

  fetchWorkspace$(projectId: number, branchName: string): Observable<MadCloudResult> {
    return this.collabApiService.fetchWorkspace$(projectId, branchName);
  }

  pullWorkspace$(body: WorkspacePullRequestBody): Observable<CollabCommitMergeResult> {
    return this.collabApiService.pullWorkspace$(body);
  }
}
